import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, PlusCircle, Search, Trash2, Clock, Filter, ArrowRight, Package, Calendar, MapPin, Truck, CheckCircle2, AlertCircle, MoreVertical, FileText, Edit2, Check, X } from 'lucide-react';


import { useAppContext } from '../../hooks/useAppContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { WorkflowRequest } from '../../types';
import { WORKFLOW_STAGES } from '../../data/mockData';
import { Input } from '../../components/ui/Input';
import useAppStore from '../../store/useAppStore';
import { differenceInHours, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';
import WorkflowRequestModal from './WorkflowRequestModal';

interface WorkflowScreenProps {
    // Props removed
}

const priorityVariant: { [key: string]: 'destructive' | 'warning' | 'default' } = {
    'عالية': 'destructive',
    'متوسطة': 'warning',
    'منخفضة': 'default',
}

// Helper function for precise time ago formatting
const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 30) return `منذ بضع ثوان`;

    let interval = seconds / 31536000;
    if (interval > 1) return `منذ ${Math.floor(interval)} سنوات`;
    interval = seconds / 2592000;
    if (interval > 1) return `منذ ${Math.floor(interval)} أشهر`;
    interval = seconds / 86400;
    if (interval > 1) return `منذ ${Math.floor(interval)} أيام`;
    interval = seconds / 3600;
    if (interval > 1) return `منذ ${Math.floor(interval)} ساعات`;
    interval = seconds / 60;
    if (interval > 1) return `منذ ${Math.floor(interval)} دقائق`;
    return `منذ ${Math.floor(seconds)} ثوان`;
};


const LastUpdatedBadge: React.FC<{ date: string }> = ({ date }) => {
    const now = new Date();
    const modifiedDate = new Date(date);
    const hoursDiff = differenceInHours(now, modifiedDate);

    let colorClass = 'bg-slate-800/50 text-slate-400 border-slate-700';
    let pulseClass = '';

    if (hoursDiff < 24) {
        colorClass = 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
        pulseClass = 'animate-pulse-green';
    } else if (hoursDiff < 168) { // Less than 7 days
        colorClass = 'bg-amber-500/10 text-amber-400 border-amber-500/20';
    }

    return (
        <div className={`text-xs flex items-center gap-1.5 px-2 py-1 rounded-full border ${colorClass}`}>
            <div className={`w-1.5 h-1.5 rounded-full ${pulseClass} ${hoursDiff < 24 ? 'bg-emerald-500' : hoursDiff < 168 ? 'bg-amber-500' : 'bg-slate-500'}`}></div>
            <Clock size={10} />
            <span>{timeAgo(date)}</span>
        </div>
    );
};

const MiniDepartureCountdown: React.FC<{ request: WorkflowRequest }> = ({ request }) => {
    if (request.currentStageId <= 2 || !request.expectedDepartureDate || isNaN(new Date(request.expectedDepartureDate).getTime())) {
        return null;
    }

    const now = new Date();
    const endDate = new Date(request.expectedDepartureDate);

    if (now > endDate) return null;

    const daysLeft = differenceInDays(endDate, now);

    let colorClass = 'text-emerald-500';
    if (daysLeft <= 3) {
        colorClass = 'text-red-500';
    } else if (daysLeft <= 7) {
        colorClass = 'text-amber-500';
    }

    const tooltipText = `يغادر خلال: ${daysLeft} يوم`;

    const radius = 8;
    const circumference = 2 * Math.PI * radius;
    // We don't have start date here, so we fake progress based on a 30-day window for visual effect
    const progress = Math.max(0, 1 - (daysLeft / 30));
    const strokeDashoffset = circumference - progress * circumference;

    return (
        <div className="absolute top-4 left-4 z-10" title={tooltipText}>
            <div className="relative flex items-center justify-center w-8 h-8 bg-slate-900/80 backdrop-blur rounded-full border border-slate-700 shadow-lg">
                <svg className="w-5 h-5 transform -rotate-90" viewBox="0 0 20 20">
                    <circle
                        className="text-slate-800"
                        stroke="currentColor" strokeWidth="2" fill="transparent" r={radius} cx="10" cy="10"
                    />
                    <circle
                        className={`${colorClass} transition-all duration-1000 ease-out`}
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="transparent" r={radius} cx="10" cy="10"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <Truck size={10} className="text-slate-300" />
                </div>
            </div>
        </div>
    );
};

// Marker used to store and parse the expected arrival date inside stage history comments
const ARRIVAL_MARKER = '[ARRIVAL_DATE]';

const getArrivalDateFromHistory = (request: WorkflowRequest): string => {
    if (!request.stageHistory || request.stageHistory.length === 0) return '';
    for (let i = request.stageHistory.length - 1; i >= 0; i--) {
        const item = request.stageHistory[i];
        if (item.comment && item.comment.startsWith(ARRIVAL_MARKER)) {
            return item.comment.replace(ARRIVAL_MARKER, '').trim();
        }
    }
    return '';
};

const upsertArrivalDateInHistory = (request: WorkflowRequest, date: string, processor: string): WorkflowRequest => {
    const currentStage = WORKFLOW_STAGES.find(s => s.id === request.currentStageId);
    const nowIso = new Date().toISOString();
    const newHistory = [...(request.stageHistory || [])];

    // Try to update the latest arrival marker if exists, else push a new history item
    let updated = false;
    for (let i = newHistory.length - 1; i >= 0; i--) {
        const item = newHistory[i];
        if (item.comment && item.comment.startsWith(ARRIVAL_MARKER)) {
            newHistory[i] = { ...item, comment: `${ARRIVAL_MARKER}${date}`, modified: { processor, timestamp: nowIso } };
            updated = true;
            break;
        }
    }

    if (!updated) {
        newHistory.push({
            stageId: request.currentStageId,
            stageName: currentStage?.name || `مرحلة ${request.currentStageId}`,
            processor,
            timestamp: nowIso,
            comment: `${ARRIVAL_MARKER}${date}`,
            documents: [],
        });
    }

    return {
        ...request,
        stageHistory: newHistory,
        lastModified: nowIso,
    };
};


const ModernWorkflowCard: React.FC<{
    request: WorkflowRequest;
    onViewDetails: () => void;
    onDelete: () => void;
}> = ({ request, onViewDetails, onDelete }) => {
    const { updateRequest } = useAppStore();
    const { user } = useAppContext();
    const currentStage = WORKFLOW_STAGES.find(s => s.id === request.currentStageId);
    const arrivalDate = request.expectedArrivalDate || getArrivalDateFromHistory(request);

    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(request.title);

    const handleSaveTitle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (editTitle.trim() === '' || editTitle === request.title) {
            setIsEditing(false);
            setEditTitle(request.title);
            return;
        }
        try {
            await updateRequest({ ...request, title: editTitle });
            toast.success('تم تغيير اسم الطلب بنجاح');
            setIsEditing(false);
        } catch (error) {
            toast.error('حدث خطأ أثناء تغيير الاسم');
        }
    };

    const handleCancelEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditTitle(request.title);
        setIsEditing(false);
    };

    return (
        <div
            onClick={onViewDetails}
            className="group relative glass-card p-5 rounded-2xl hover:bg-slate-800/50 transition-all duration-300 cursor-pointer border border-slate-700/50 hover:border-blue-500/30 overflow-hidden"
        >
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <MiniDepartureCountdown request={request} />

            <div className="flex justify-between items-start mb-4 pl-8">
                <div className="flex items-start gap-3">
                    <div className={`p-2.5 rounded-xl ${request.type === 'استيراد' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                        {request.type === 'استيراد' ? <Download size={20} /> : <Truck size={20} />}
                    </div>
                    <div>
                        {isEditing ? (
                            <div className="flex items-center gap-2 mb-1" onClick={e => e.stopPropagation()}>
                                <Input
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    className="h-8 py-1 px-2 text-sm bg-slate-800 border-slate-600 text-white w-48"
                                    autoFocus
                                />
                                <button
                                    onClick={handleSaveTitle}
                                    className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                                >
                                    <Check size={14} />
                                </button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 group/title">
                                <h3 className="font-bold text-white text-lg leading-tight mb-1 group-hover:text-blue-400 transition-colors">{request.title}</h3>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditing(true);
                                    }}
                                    className="opacity-0 group-hover/title:opacity-100 p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
                                >
                                    <Edit2 size={12} />
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                            <span>{request.id}</span>
                            <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                            <span>{new Date(request.creationDate).toLocaleDateString('en-GB')}</span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                    <Badge variant={priorityVariant[request.priority]} className="shadow-sm">{request.priority}</Badge>
                </div>
            </div>

            <div className="space-y-4 mb-4">
                <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/30">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-slate-400">المرحلة الحالية</span>
                        <span className="text-xs font-semibold text-blue-400">{currentStage?.name}</span>
                    </div>

                    {/* 7-Stage Progress Indicator */}
                    <div className="flex gap-1.5 mt-2">
                        {WORKFLOW_STAGES.map((stage) => {
                            const isCompleted = request.currentStageId > stage.id;
                            const isCurrent = request.currentStageId === stage.id;

                            let bgClass = 'bg-slate-700';
                            if (isCompleted) bgClass = 'bg-blue-500';
                            if (isCurrent) bgClass = 'bg-blue-400 animate-pulse';

                            return (
                                <div
                                    key={stage.id}
                                    className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${bgClass}`}
                                    title={stage.name}

                                />
                            );
                        })}
                    </div>

                    <div className="flex justify-between mt-1">
                        <span className="text-[10px] text-slate-500">البداية</span>
                        <span className="text-[10px] text-slate-500">النهاية</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <Calendar size={12} className="text-slate-500" />
                        <div>
                            <div className="text-slate-500 text-[10px]">المغادرة</div>
                            <div className="font-mono text-slate-300">{request.expectedDepartureDate ? new Date(request.expectedDepartureDate).toLocaleDateString('en-GB') : '-'}</div>
                        </div>
                    </div>
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <MapPin size={12} className="text-slate-500" />
                        <div>
                            <div className="text-slate-500 text-[10px]">الوصول</div>
                            <div className="font-mono text-slate-300">{arrivalDate ? new Date(arrivalDate).toLocaleDateString('en-GB') : '-'}</div>
                        </div>
                    </div>
                </div>
                {/* BL Details: Number & Date */}
                <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <FileText size={12} className="text-blue-400" />
                        <div>
                            <div className="text-slate-500 text-[10px]">رقم البوليصة (BL)</div>
                            <div className="font-mono text-white font-bold">{request.blNumber || '---'}</div>
                        </div>
                    </div>
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <Calendar size={12} className="text-blue-400" />
                        <div>
                            <div className="text-slate-500 text-[10px]">تاريخ البوليصة</div>
                            <div className="font-mono text-white font-bold">{request.blDate ? new Date(request.blDate).toLocaleDateString('en-GB') : '---'}</div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                    {/* Invoice Number Display */}
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <FileText size={12} className="text-emerald-400" />
                        <div>
                            <div className="text-slate-500 text-[10px]">رقم الفاتورة</div>
                            <div className="font-mono text-white font-bold">{request.invoiceNumber || '---'}</div>
                        </div>
                    </div>
                    {/* Goods Type Display */}
                    <div className="bg-slate-800/30 p-2 rounded-lg border border-slate-700/30 flex items-center gap-2">
                        <Package size={12} className="text-purple-400" />
                        <div>
                            <div className="text-slate-500 text-[10px]">نوع البضاعة</div>
                            <div className="font-mono text-white font-bold">{request.goodsType || '---'}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-700/30">
                {request.lastModified && !isNaN(new Date(request.lastModified).getTime()) ? (
                    <LastUpdatedBadge date={request.lastModified} />
                ) : (
                    <span></span>
                )}

                <div className="flex items-center gap-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        className="p-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        title="حذف الطلب"
                    >
                        <Trash2 size={16} />
                    </button>
                    <button className="flex items-center gap-1 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                        التفاصيل <ArrowRight size={14} className="rotate-180" />
                    </button>
                </div>
            </div>
        </div >
    )
}

const ModernWorkflowScreen: React.FC<WorkflowScreenProps> = () => {
    const { t, user } = useAppContext();
    const { requests, isWorkflowModalOpen, setWorkflowModalOpen, deleteRequest, openConfirmation } = useAppStore();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [stageFilter, setStageFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');

    const handleDeleteRequest = (requestId: string) => {
        if (!user) return;
        openConfirmation(
            'هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.',
            () => deleteRequest(requestId, user.employeeId)
        );
    };

    const filteredRequests = requests.filter(req => {
        const matchesSearch = req.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.id.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === 'all' || req.type === typeFilter;
        const matchesStage = stageFilter === 'all' || req.currentStageId.toString() === stageFilter;
        const matchesPriority = priorityFilter === 'all' || req.priority === priorityFilter;
        return matchesSearch && matchesType && matchesStage && matchesPriority;
    });

    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-content-fade-in pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-xl bg-slate-800/50 hover:bg-slate-700 text-slate-400 hover:text-white transition-all border border-slate-700/50"
                    >
                        <ArrowRight size={20} />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg shadow-blue-500/20">
                                <Package size={28} className="text-white" />
                            </div>
                            {t('importExport')}
                        </h1>
                        <p className="text-slate-400">إدارة ومتابعة طلبات الاستيراد والتصدير وسير العمل</p>
                    </div>
                </div>
                <button
                    onClick={() => setWorkflowModalOpen(true)}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 transition-all bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700"
                >
                    <PlusCircle size={20} />
                    <span>{t('createNewRequest')}</span>
                </button>
            </div>

            {/* Filters */}
            <div className="glass-panel p-4 rounded-2xl flex flex-col lg:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="بحث في الطلبات..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-10 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                    />
                </div>
                <div className="flex gap-2 w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0">
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-slate-300 focus:outline-none focus:border-blue-500/50 transition-all min-w-[120px]"
                    >
                        <option value="all">كل الأنواع</option>
                        <option value="استيراد">استيراد</option>
                        <option value="تصدير">تصدير</option>
                    </select>
                    <select
                        value={stageFilter}
                        onChange={(e) => setStageFilter(e.target.value)}
                        className="bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-slate-300 focus:outline-none focus:border-blue-500/50 transition-all min-w-[140px]"
                    >
                        <option value="all">كل المراحل</option>
                        {WORKFLOW_STAGES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 px-4 text-slate-300 focus:outline-none focus:border-blue-500/50 transition-all min-w-[120px]"
                    >
                        <option value="all">كل الأولويات</option>
                        <option value="عالية">عالية</option>
                        <option value="متوسطة">متوسطة</option>
                        <option value="منخفضة">منخفضة</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            {filteredRequests.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredRequests.map(req =>
                        <ModernWorkflowCard
                            key={req.id}
                            request={req}
                            onViewDetails={() => navigate(`/workflow/${req.id}`)}
                            onDelete={() => handleDeleteRequest(req.id)}
                        />
                    )}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-24 h-24 bg-slate-800/50 rounded-full flex items-center justify-center mb-6 animate-pulse">
                        <Package size={48} className="text-slate-600" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">لا توجد طلبات</h3>
                    <p className="text-slate-400 max-w-md mx-auto mb-8">
                        {searchTerm || typeFilter !== 'all' || stageFilter !== 'all' || priorityFilter !== 'all'
                            ? 'لا توجد نتائج تطابق معايير البحث الخاصة بك.'
                            : 'لم يتم إنشاء أي طلبات استيراد أو تصدير بعد. ابدأ بإنشاء طلب جديد.'}
                    </p>
                    <button
                        onClick={() => setWorkflowModalOpen(true)}
                        className="px-6 py-2.5 rounded-xl bg-slate-800 text-white hover:bg-slate-700 border border-slate-700 transition-all flex items-center gap-2"
                    >
                        <PlusCircle size={18} />
                        <span>إنشاء طلب جديد</span>
                    </button>
                </div>
            )}

            <WorkflowRequestModal isOpen={isWorkflowModalOpen} />
        </div>
    );
};

export default ModernWorkflowScreen;
